import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, LinearProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  metric: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[2]
    }
  },
  metricLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  metricValue: {
    fontSize: '1.25rem',
    fontWeight: 500
  },
  chart: {
    marginTop: theme.spacing(2)
  },
  chartItem: {
    marginBottom: theme.spacing(2),
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateX(4px)'
    }
  },
  chartLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5)
  },
  chartLabelLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
      transition: 'width 1s ease-in-out'
    }
  },
  list: {
    marginTop: theme.spacing(2)
  },
  infoIcon: {
    fontSize: '1rem',
    color: theme.palette.primary.main,
    cursor: 'help',
    marginLeft: theme.spacing(0.5)
  },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius
  },
  themesList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    '& > li': {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,
      '&:last-child': {
        marginBottom: 0
      }
    }
  },
  themeText: {
    color: theme.palette.text.primary,
    fontSize: '1rem',
    lineHeight: 1.5
  }
}));

const AnalysisSection = ({ content, metrics, themes, questionType }) => {
  const classes = useStyles();
  const [hoveredMetric, setHoveredMetric] = React.useState(null);

  // Transform metrics for visualization based on question type
  const chartData = React.useMemo(() => {
    if (!metrics) return [];

    switch (questionType) {
      case 'scale':
      case 'scale_5':
      case 'scale_10':
        return metrics.averageRating ? [{
          name: 'Average Rating',
          value: metrics.averageRating,
          tooltip: `Average rating: ${metrics.averageRating}`
        }] : [];
      
      case 'binary':
        return Object.entries({
          Yes: metrics.yesPercentage,
          No: metrics.noPercentage
        })
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({
          name,
          value,
          tooltip: `${value}% responded ${name}`
        }));
      
      case 'multiple':
      case 'multiple_rating':
        return Object.entries(metrics.optionDistribution || {})
          .filter(([_, percentage]) => percentage > 0)
          .map(([option, percentage]) => ({
            name: option,
            value: percentage,
            tooltip: `${percentage}% selected ${option}`
          }));
      
      default:
        // For open-ended questions, show theme distribution if available
        return (themes || [])
          .filter(theme => theme && theme.percentage > 0)
          .map((theme, index) => ({
            name: theme.name || `Theme ${index + 1}`,
            value: theme.percentage || 0,
            tooltip: `${theme.percentage}% of responses mentioned this theme`
          }));
    }
  }, [metrics, questionType, themes]);

  // Don't render if no meaningful content
  if ((!content || content.length === 0) && (!metrics || Object.keys(metrics).length === 0)) {
    return null;
  }

  const getMetricTooltip = (key, value) => {
    switch (key) {
      case 'totalResponses':
        return `Total number of responses received for this question`;
      case 'averageRating':
        return `Average rating across all responses`;
      case 'responseRate':
        return `Percentage of participants who responded`;
      default:
        return `Metric value: ${value}`;
    }
  };

  // Filter out metrics with zero or undefined values
  const filteredMetrics = Object.entries(metrics || {})
    .filter(([_, value]) => value !== undefined && value !== null && value !== 0)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        Analysis
      </Typography>
      <Box className={classes.content}>
        {/* Metrics Section */}
        {Object.keys(filteredMetrics).length > 0 && (
          <Box className={classes.metrics}>
            {Object.entries(filteredMetrics).map(([key, value]) => (
              <Box 
                key={key}
                className={classes.metric}
                onMouseEnter={() => setHoveredMetric(key)}
                onMouseLeave={() => setHoveredMetric(null)}
              >
                <Typography className={classes.metricLabel}>
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  <Tooltip 
                    title={getMetricTooltip(key, value)}
                    placement="top"
                    classes={{ tooltip: classes.tooltip }}
                  >
                    <InfoIcon className={classes.infoIcon} />
                  </Tooltip>
                </Typography>
                <Typography className={classes.metricValue}>
                  {typeof value === 'number' && !isNaN(value) ? 
                    (key.toLowerCase().includes('percentage') ? `${value}%` : value) 
                    : value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Chart Section */}
        {chartData.length > 0 && (
          <Box className={classes.chart}>
            <Typography variant="subtitle2" gutterBottom>Distribution</Typography>
            {chartData.map((item, index) => (
              <Box key={index} className={classes.chartItem}>
                <Box className={classes.chartLabel}>
                  <Box className={classes.chartLabelLeft}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Tooltip 
                      title={item.tooltip}
                      placement="top"
                      classes={{ tooltip: classes.tooltip }}
                    >
                      <InfoIcon className={classes.infoIcon} />
                    </Tooltip>
                  </Box>
                  <Typography variant="body2">{item.value}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={item.value}
                  className={classes.progressBar}
                  color="primary"
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Themes/Content Section */}
        {content && content.length > 0 && (
          <ul className={classes.themesList}>
            {content.map((item, index) => (
              <li key={index}>
                <Typography className={classes.themeText}>
                  {item}
                </Typography>
              </li>
            ))}
          </ul>
        )}
      </Box>
    </Paper>
  );
};

AnalysisSection.propTypes = {
  content: PropTypes.string,
  metrics: PropTypes.object,
  themes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    percentage: PropTypes.number
  })),
  questionType: PropTypes.string
};

AnalysisSection.defaultProps = {
  metrics: {},
  themes: [],
  questionType: 'default'
};

export default AnalysisSection; 